export function isIpAddress(hostname: string): boolean {
  return /^(\d{1,3}(\.\d{1,3}){3}|\[[0-9a-f:]+\])$/i.test(hostname);
}

// Country registries nest registrants under these labels (tfl.gov.uk, example.edu.au), hand-picked so the UI doesn’t bundle the whole public suffix list.
const COUNTRY_SECOND_LEVEL_LABELS = new Set([
  "ac",
  "biz",
  "co",
  "com",
  "edu",
  "go",
  "gob",
  "gouv",
  "gov",
  "info",
  "int",
  "ltd",
  "mil",
  "name",
  "ne",
  "net",
  "nom",
  "or",
  "org",
  "plc",
  "sch",
]);

// Registries outside that pattern, from the public suffix list for the countries we see most.
const COUNTRY_PUBLIC_SUFFIXES = new Set([
  "me.uk",
  "nhs.uk",
  "police.uk",
  "asn.au",
  "id.au",
  "act.au",
  "nsw.au",
  "nt.au",
  "qld.au",
  "sa.au",
  "tas.au",
  "vic.au",
  "wa.au",
  "act.edu.au",
  "catholic.edu.au",
  "nsw.edu.au",
  "nt.edu.au",
  "qld.edu.au",
  "sa.edu.au",
  "tas.edu.au",
  "vic.edu.au",
  "wa.edu.au",
  "qld.gov.au",
  "sa.gov.au",
  "tas.gov.au",
  "vic.gov.au",
  "wa.gov.au",
  "cri.nz",
  "geek.nz",
  "gen.nz",
  "govt.nz",
  "health.nz",
  "iwi.nz",
  "kiwi.nz",
  "maori.nz",
  "parliament.nz",
  "school.nz",
  "firm.in",
  "gen.in",
  "ind.in",
  "nic.in",
  "res.in",
  "law.za",
  "ngo.za",
  "nic.za",
  "school.za",
  "web.za",
  "ad.jp",
  "ed.jp",
  "gr.jp",
  "lg.jp",
]);

// Under gTLDs the generic labels are registrable names themselves, like go.com and name.com.
function registrableLabelCount(labels: string[]): number {
  const suffix = (count: number) => labels.slice(-count).join(".");
  if (labels.length > 3 && COUNTRY_PUBLIC_SUFFIXES.has(suffix(3))) return 4;
  if (labels.length < 3 || labels.at(-1)!.length !== 2) return 2;
  return COUNTRY_SECOND_LEVEL_LABELS.has(labels.at(-2)!) ||
    COUNTRY_PUBLIC_SUFFIXES.has(suffix(2))
    ? 3
    : 2;
}

export function rootDomain(hostname: string): string {
  if (isIpAddress(hostname)) return hostname;
  const parts = hostname.split(".");
  const labels = registrableLabelCount(parts);
  return parts.length > labels ? parts.slice(-labels).join(".") : hostname;
}

export function domainFromUrlPattern(urlPattern: string): string | null {
  // Strip common regex anchors and unescape dots: ^https://api\.tines\.com → https://api.tines.com
  const cleaned = urlPattern.replace(/^\^/, "").replaceAll("\\.", ".");
  try {
    return rootDomain(new URL(cleaned).hostname);
  } catch {
    return null;
  }
}

export function domainSearchToken(targetUrl: string): string | undefined {
  let host: string;
  try {
    host = new URL(targetUrl).hostname;
  } catch {
    return undefined;
  }
  if (isIpAddress(host)) return undefined;
  const labels = host.split(".").filter(Boolean);
  if (labels.length < 2) return undefined;
  const token = labels[labels.length - registrableLabelCount(labels)]!;
  return token.length >= 3 ? token : undefined;
}
